/**
 * Brand Configuration
 * This file defines all brand-specific text, URLs, and identifiers in a single configuration object.
 * To rebrand the application, simply edit the values in BASE_CONFIG.
 * 
 * @typedef {import('../types/theme').BrandConfig} BrandConfig
 */

// Environment detection
const isDevelopment = process.env.NODE_ENV === "development";
const isProduction = process.env.NODE_ENV === "production";

// Base configuration
const BASE_CONFIG = {
  // Brand identity
  name: "Monarch Displays",
  shortName: "Monarch Displays",
  adminPortalLabel: "Admin Portal",
  
  // URLs
  websiteUrl: process.env.CLIENT_SIDE_SERVICE_NODE || "https://monarchdisplays.com",
  logoUrl: "/company-logo.jpg",
  
  // Email addresses
  emails: {
    support: "support@monarchdisplays.com",
    sales: "sales@monarchdisplays.com",
    admin: "admin@monarchdisplays.com",
    noreply: "noreply@monarchdisplays.com",
  },
  
  // API endpoints
  api: {
    identity: process.env.REACT_APP_URL__identity_microservices || "https://api.monarchdisplays.com/app-identity/api/v2",
    admin: process.env.REACT_APP_URL__admin_microservices || "https://api.monarchdisplays.com/admin-services/api/v2",
    application: process.env.REACT_APP_URL__application_microservices || "https://api.monarchdisplays.com/app-services/api/v2",
    utility: process.env.REACT_APP_URL__utility_microservices || "https://api.monarchdisplays.com/utility-services/api/v2",
  },
  
  // WebSocket configuration
  websocket: {
    url: process.env.REACT_APP_WEBSOCKET_URL || "wss://api.monarchdisplays.com",
  },
  
  // Media storage
  media: {
    s3Bucket: "web2printsolution-media",
    s3Region: "us-east-1",
    cdnUrl: "https://web2printsolution-media.s3.us-east-1.amazonaws.com",
  },
};

// Development overrides
const DEVELOPMENT_CONFIG = {
  ...BASE_CONFIG,
  api: {
    identity: process.env.REACT_APP_URL__identity_microservices || "http://localhost:5001/app-identity/api/v2",
    admin: process.env.REACT_APP_URL__admin_microservices || "http://localhost:5003/admin-services/api/v2",
    application: process.env.REACT_APP_URL__application_microservices || "http://localhost:5002/app-services/api/v2",
    utility: process.env.REACT_APP_URL__utility_microservices || "http://localhost:5004/utility-services/api/v2",
  },
  websocket: {
    url: process.env.REACT_APP_WEBSOCKET_URL || "ws://localhost:5002",
  },
};

/**
 * Export appropriate configuration based on environment
 * @type {BrandConfig}
 */
export const BRAND = isDevelopment ? DEVELOPMENT_CONFIG : BASE_CONFIG;

/**
 * Helper function to validate URL format
 * @param {string} string - URL string to validate
 * @returns {boolean} True if valid URL, false otherwise
 */
const isValidUrl = (string) => {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

/**
 * Helper function to validate email format
 * @param {string} email - Email address to validate
 * @returns {boolean} True if valid email, false otherwise
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validation function for brand configuration
 * Checks that all required fields are present and properly formatted
 * 
 * @param {BrandConfig} brand - Brand configuration to validate
 * @returns {boolean} True if valid, false otherwise
 * 
 * @example
 * if (validateBrand(BRAND)) {
 *   console.log('Brand configuration is valid');
 * }
 */
export const validateBrand = (brand) => {
  const errors = [];
  
  // Check required fields
  const requiredFields = ["name", "shortName", "websiteUrl"];
  requiredFields.forEach((field) => {
    if (!brand[field]) {
      errors.push(`Missing required brand field: ${field}`);
    }
  });
  
  // Validate URLs
  const urlFields = ["websiteUrl"];
  urlFields.forEach((field) => {
    if (brand[field] && !isValidUrl(brand[field])) {
      errors.push(`Invalid URL format for ${field}: ${brand[field]}`);
    }
  });
  
  // Validate logo URL (can be relative path or full URL)
  if (brand.logoUrl && !brand.logoUrl.startsWith('/') && !isValidUrl(brand.logoUrl)) {
    errors.push(`Invalid logo URL format: ${brand.logoUrl}. Must be a relative path (starting with /) or a full URL.`);
  }
  
  // Validate emails
  if (brand.emails) {
    Object.entries(brand.emails).forEach(([key, email]) => {
      if (email && !isValidEmail(email)) {
        errors.push(`Invalid email format for emails.${key}: ${email}`);
      }
    });
  }
  
  // Validate API endpoints
  if (brand.api) {
    Object.entries(brand.api).forEach(([key, url]) => {
      if (url && !isValidUrl(url)) {
        errors.push(`Invalid API URL for api.${key}: ${url}`);
      }
    });
  }
  
  if (errors.length > 0) {
    console.error("Brand validation errors:", errors);
    return false;
  }
  
  return true;
};

// Export individual values for convenience
export const BRAND_NAME = BRAND.name;
export const BRAND_SHORT_NAME = BRAND.shortName;
export const BRAND_ADMIN_PORTAL_LABEL = BRAND.adminPortalLabel;
export const BRAND_WEBSITE_URL = BRAND.websiteUrl;
export const BRAND_LOGO_URL = BRAND.logoUrl;
export const BRAND_EMAILS = BRAND.emails;
export const BRAND_API = BRAND.api;
export const BRAND_WEBSOCKET = BRAND.websocket;
export const BRAND_MEDIA = BRAND.media;
